import { ComponentType } from '@angular/cdk/overlay';
import { Component, computed, effect, ElementRef, EmbeddedViewRef, inject, Signal, signal, TemplateRef, untracked, viewChild, ViewContainerRef, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BigNumber } from 'bignumber.js';
import {
    Chart,
    ChartOptions, ChartScrollablePlotAreaOptions,
    PlotOptions, Point, PointEventsOptionsObject, PointInteractionEventObject, SelectEventObject, SeriesOptions, XAxisOptions, YAxisOptions
} from 'highcharts';
import { Series } from 'highcharts/highcharts.src';
import { tap, timer } from 'rxjs';
import { DataDisplayOptions, DataNumberFormat, NumberRoundingMethod, SortDirection } from '../../../services/api.services';
import { isNullOrUndefined } from '../../../shared/utils';
import { distinctValues } from '../../../shared/utils/distinctValues';
import { DisplayFormatArgs } from '../../formatting/DataFormatter';
import { debounced } from '../../utils/debounced';
import { logger } from '../../utils/logger';
import { dataComparer } from '../agGrid/pivotTable/dataComparer';
import { VisualComponent } from '../base/Visual.component';
import { TypeOfValue } from '../base/VisualStyleEditor.component';
import { Rows, RowsResult, Values } from '../data';
import { DataTooltip, DataTooltipColumn, DataTooltipHeader, DataTooltipRow, DataTooltipRowStyle } from '../tooltip/DataTooltipComponent';
import { Visual } from '../Visual';
import { VisualContext } from '../VisualContext';
import { SerializationOptions, Serialize } from '../VisualDataSerializer';
import { VisualField, VisualFieldSortingInit } from '../VisualField';
import { VisualFieldRoleType, VisualFieldRoleTypeOrKind } from '../VisualFieldRoleType';
import { VisualFieldRoleTypeMap } from '../VisualFieldRoleTypeMap';
import { VisualFieldSorting } from '../VisualFieldSorting';
import { VisualHost } from '../VisualHost';
import { DataPoint, DataPointField } from '../VisualInteractions';
import { FontSize, TextElementStyle, TextStyle, VisualStyle } from '../VisualStyle';
import { VisualType, VisualTypeOptions } from '../VisualType';
import { HighchartsChart, PbirHighchartsChartComponent } from './base/PbirHighchartsChart.component';
import { ChartStyleEditorComponent } from './ChartVisualStyleEditor.component';

const LOGGER = logger('ChartVisualComponent');

const CUSTOM_LEGEND = true;

@Component({ template: '' })
export abstract class ChartVisualComponent<
  TSeries extends SeriesOptions = SeriesOptions,
  TOptions extends Visual.Options = ChartVisual.Options,
  TState extends ChartVisual.State = ChartVisual.State,
  TStyle extends ChartVisual.Style = ChartVisual.Style>
  extends VisualComponent<TOptions, TState, TStyle, VisualComponent.DefaultData> {
  readonly hostElement = inject(ElementRef<HTMLElement>) as ElementRef<HTMLElement>;
  
  override get style(): WritableSignal<TStyle> { return super.style; }
  override get state(): WritableSignal<TState> { return super.state; }
  override get options(): Signal<TOptions> { return super.options; }

  override applyDefaultSorting(fields: VisualField[]) {

    let axiesDateFieldIndex = fields.findIndex((f) => f.dataType.isDateOrDateTime && f.role().type.title == VisualFieldRoleType.Defaults.groupingAxis.title);
    if (axiesDateFieldIndex != -1) {
      let field = fields[axiesDateFieldIndex]

      fields[axiesDateFieldIndex] = new VisualField(
        field.target,
        field.visualFieldId,
        <VisualFieldSortingInit>{
          sorted: true,
          sortDirection: SortDirection.Ascending,
          sortOrder: 0
        }
      );

      return fields;
    }

    let mesureField = fields.findIndex((f) => f.role().type.title == VisualFieldRoleType.Defaults.measures.title);
    if (mesureField != -1) {
     let field = fields[mesureField]

      fields[mesureField] = new VisualField(
        field.target,
        field.visualFieldId,
        <VisualFieldSortingInit>{
          sorted: true,
          sortDirection: SortDirection.Descending,
          sortOrder: 0
        }
      );

      return fields;
    }

    return fields
  }

  readonly chart = viewChild('highcharts', { read: PbirHighchartsChartComponent });

  abstract get chartType(): string;

  readonly returnedMaxRows = computed(() => this.computeReturnedMaxRows());
  computeReturnedMaxRows() { return undefined; }

  abstract get measuresRoleType(): VisualFieldRoleType;
  abstract get groupingRoleType(): VisualFieldRoleType;
  abstract get legendRoleType(): VisualFieldRoleType;

  protected getRole(typeOrKind: VisualFieldRoleTypeOrKind | string, enabledOnly = true) {
    let role = this.visual()?.fieldRoles.find(typeOrKind);
    if (role && (!enabledOnly || role.isEnabled()))
      return role;

    return null;
  }

  readonly measuresRole = computed(() => this.getRole(this.measuresRoleType));
  readonly groupingRole = computed(() => this.getRole(this.groupingRoleType));
  readonly legendRole = computed(() => this.getRole(this.legendRoleType));

  readonly measureFields = computed(() => [...this.measuresRole()?.fields.exludingInvalidCalc() ?? []]);
  readonly useLegendAsGrouping = computed(() => !this.groupingRole()?.fields.exludingInvalidCalc().length && this.legendRole()?.fields.exludingInvalidCalc().length);
  readonly onlyMeasures = computed(() => !this.groupingFields()?.length && !this.legendFields()?.length && !!this.measureFields()?.length);

  readonly groupingFields = computed(() => {
    let fields = this.useLegendAsGrouping()
      ? this.legendRole()?.fields.exludingInvalidCalc()
      : this.groupingRole()?.fields.exludingInvalidCalc();

    return [...(fields ?? [])];
  });

  readonly legendFields = computed(() => {
    if (this.useLegendAsGrouping())
      return [];

    return [...(this.legendRole()?.fields.exludingInvalidCalc() ?? [])];
  });

  readonly hasLegend = computed(() => this.isVisualValid() && this.legendFields().length > 0);
  readonly legends = computed(() => {
    if (!this.hasLegend())
      return null;

    let rowsFormatter = this.rowsFormatter();
    if (!rowsFormatter)
      return null;
      
    let rows = this.rowsResult();

    return this.legendFields().map(f => {
      let l = new ChartLegend(f, rowsFormatter);
      l.addItemsFromRows(rows.rows);
      return l;
    });
  });

  private readonly setExceededRowLimits = effect(() => {
    this.exceededRowLimits.set(!!this.data.exceededRowLimits());
  });

  readonly rows = computed(() => {
    let limit = this.returnedMaxRows();
    let take  = limit ? limit : undefined;
    return this.data.rows({ take });
  });

  readonly rowsResult = computed(() => {
    let rows = this.rows();
    if (!rows?.hasResult())
      return null;

    let result = rows.result();
    return result;
  });

  readonly rowsFormatter = computed(() => {
    let result = this.rowsResult();
    if (!result)
      return null;

    return this.formatter(result);
  });


  readonly loading = computed(() => this.data.loading());
  readonly loaded = computed(() => !!this.rows()?.success());
  readonly failed = computed(() => this.data.failure() || !!this.rows()?.failure());

  readonly anyData = computed(() => this.loaded() && !!this.rows().result()?.rows?.length);
  readonly canRender = computed(() => this.canRenderCheck());

  protected canRenderCheck() {
    return this.isVisualValid() && this.loaded() && this.anyData();
  }

  readonly canRenderBefore = signal(false);
  private canRenderStarted = effect(() => {
    let [curr, prev] = [this.canRender(), untracked(() => this.canRenderBefore())];
    if (prev && !curr) {
      this.broadcastRenderStarted();
    }

    this.canRenderBefore.set(curr);
  });

  readonly loadedBefore = signal(false);
  private loadedStarted = effect(() => {
    let [curr, prev] = [this.loaded(), untracked(() => this.loadedBefore())];
    if (prev && !curr) {
      this.broadcastRenderStarted();
    }

    this.loadedBefore.set(curr);
  });

  broadcastRenderStarted() {
    this.broadcastEvent({ type: VisualHost.EventType.RenderStart });
  }


  private renderCompleteOnErrorOrNoData = effect(() => {
    if ((this.loaded() && !this.anyData()) || this.failed()) {
      let failed = this.failed();
      let error = failed ? untracked(() => this.rows()?.error() ?? this.data?.query()?.error()) : null;

      this.broadcastEvent({
        type: VisualHost.EventType.RenderComplete,
        failed,
        error
      });
    }
  });


  protected override onSelectionChange(e) {
    super.onSelectionChange(e);
    untracked(() => {
      this.customLegend.redraw.update(x => x + 1);
    });
  }

  protected override onSelectionCleared(e) {
    untracked(() => {
      super.onSelectionCleared(e);

      let h = this.chart()?.chart();
      if (h) {
        h.getSelectedPoints().forEach(x => x.select(false));
      }
    });
  }


  readonly chartOptions = computed<ChartOptions>(() => this.computeChartOptions());

  computeChartOptions(): ChartOptions {
    let style = this.style();

    let component = this;

    return {
      type: this.chartType,
      animation: !this.disableAnimations,
      backgroundColor: style?.backgroundColor() ?? '#ffffff',
      events: {
        load: function (this: Chart) {
          component.broadcastRenderStarted();
          component.onChartLoad(this);
        },
 
        redraw: function (this: Chart) {
          component.onChartRedraw(this);
        },
        
        render: () => {
          this.broadcastEvent({ type: VisualHost.EventType.RenderComplete });
          this.onChartRender();
          component.updatePointsSelectedDebounced();
        },
        
        selection: function (this: Chart, event: SelectEventObject) {
          component.updatePointsSelectedDebounced();
          return undefined;
        },
      },

      ...this.chartSpacing(),

      scrollablePlotArea: this.computeScrollablePlotArea(),
    };
  }

  protected computeScrollablePlotArea(): ChartScrollablePlotAreaOptions {
    return undefined;
  }

  protected onChartLoad(chart: Chart) { }


  readonly chartSpacing = computed(() => {
    let baseSpacing = this.getBaseSpacing();

    let t = baseSpacing;
    let l = baseSpacing;
    let b = baseSpacing;
    let r = baseSpacing;

    if (this.dataLabels()?.enabled) {
      let dataLabelsSpacing = this.getSpacingForDataLabels();

      let legendAlignment = this.legendAlignment();
      let isHorizontal = this.isHorizontalPlot();

      if (isHorizontal) {
        if (legendAlignment != ChartVisual.Alignment.Left)
          l = dataLabelsSpacing;

        if (legendAlignment != ChartVisual.Alignment.Right)
          r = dataLabelsSpacing;
      }
      else {
        if (legendAlignment != ChartVisual.Alignment.Top)
          t = dataLabelsSpacing;

        if (legendAlignment != ChartVisual.Alignment.Bottom)
          b = dataLabelsSpacing;
      }
    }

    return {
      spacingTop: t,
      spacingLeft: l,
      spacingBottom: b,
      spacingRight: r + this.getAdditionalRightSpacing(),
    };
  });

  getBaseSpacing() {
    return 5;
  }

  getSpacingForDataLabels() {
    return 10;
  }

  getAdditionalRightSpacing() {
    return 0;
  }


  readonly plotOptions = computed<PlotOptions>(() => this.computePlotOptions());

  computePlotOptions(): PlotOptions {
    let component = this;

    return {
      series: {
        ...{
          animation: !this.disableAnimations,
          allowPointSelect: true,
          point: {
            events: {
              ...this.getPointSelectEvents(),
            }
          },
          states: {
            select: {
              color: undefined,
            }
          }
        },
      },
    };
  }

  private getPointSelectEvents(): Partial<PointEventsOptionsObject> {
    let component = this;

    return {
      mouseOut: function (this: Point, e) {
        component.onPointMouseOut(this, e);
      },

      mouseOver: function (this: Point, e) {
        component.onPointMouseOver(this, e);
      },

      select: function (this: Point, e) {
        try {
          component.onPointSelect(this, e);

          let data = this;

          let custom = (data as any).custom as DataTooltipPointCustomData;
          let dataPoints = 'dataPoint'  in custom ? [custom.dataPoint]
                         : 'dataPoints' in custom ? custom.dataPoints
                         : null;

          if (dataPoints?.length) {
            if (e.accumulate) {
              component.selectionAdd(dataPoints);
            }
            else {
              component.selectionSet(dataPoints);
            }
          }
        }
        finally {
          component.updatePointsSelected();
        }
      },
      
      unselect: function (this: Point, e) {
        try {
          component.onPointUnselect(this, e);

          let data = this;

          let custom = (data as any).custom as DataTooltipPointCustomData;
          let dataPoints = 'dataPoint'  in custom ? [custom.dataPoint]
                         : 'dataPoints' in custom ? custom.dataPoints
                         : null;

          if (dataPoints?.length)
            component.selectionRemove(dataPoints);
        }
        finally {
          component.updatePointsSelectedOnUnselect();
        }
      },
    };
  }
  
  protected onPointMouseOut(point: Point, e: PointerEvent) {
    for (let label of this.getLabelGraphicElements(point)) {
      label.classList.remove('highcharts-series-hover');
    }
  }

  protected onPointMouseOver(point: Point, e: Event) {
    for (let label of this.getLabelGraphicElements(point)) {
      label.classList.add('highcharts-series-hover');
    }
  }

  protected onPointSelect(point: Point, e: PointInteractionEventObject) {
    for (let label of this.getLabelGraphicElements(point)) {
      label.classList.add('pbir-point-selected');
    }
  }
  
  protected onPointUnselect(point: Point, e: PointInteractionEventObject) {
    for (let label of this.getLabelGraphicElements(point)) {
      label.classList.remove('pbir-point-selected');
    }
  }

  protected getLabelGraphicElements(point: Point): HTMLElement[] {
    let p = (point as any);
    let labels = [p.dataLabel, p.node?.parentNode?.point?.dataLabel].filter(x => !!x);
    return labels.flatMap(x => [x.element, x.connector?.element]).filter(x => !!x);
  }



  readonly pointsSelected = signal(false);

  private updatePointsSelected() {
    let hc = this.chart()?.chart();

    let selected = (
      !!hc?.getSelectedPoints()?.length ||
      !!hc?.getSelectedSeries()?.length
    );

    this.pointsSelected.set(selected);
    this.applyWrapperHasSelectionClass();
  }

  private readonly updatePointsSelectedDebounced = debounced(() => this.updatePointsSelected(), 50, this.destroyRef);

  private updatePointsSelectedOnUnselect() {
    this.updatePointsSelectedDebounced();
  }

  readonly setNonDataPointSelectionActive_OnPointsSelected = effect(() => this.setNonDataPointSelectionActive(this.pointsSelected()));


  // HACK: Data Labels' 'shape' does not re-render on change unless data labels are toggled
  private readonly plotOptionsDataLabels = signal<Highcharts.DataLabelsOptions>(null);
  private readonly plotOptionsDataLabelsUpdate = effect(() => {
    let dataLabels = this.dataLabels();
    this.plotOptionsDataLabels.set({
      enabled: false
    });

    if (dataLabels) {
      timer(0)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.plotOptionsDataLabels.set(dataLabels));
    }
  });

  readonly xAxisStyle = computed(() => this.style()?.xAxis());
  readonly xAxis = computed<XAxisOptions>(() => this.computeXAxis());

  computeXAxis(): XAxisOptions {
    if (!this.isVisualValid())
      return null;

    let axisLabelsStyle = this.style()?.axisLabels();
    let xAxisStyle = this.style()?.xAxis();

    return {
      labels: {
        enabled: !!xAxisStyle?.enabled,
        useHTML: false,
        style: {
          ...TextStyle.toHighchartsStyle(xAxisStyle),
        }
      },

      type: 'category',

      title: {
        text: '___',
        style: TextStyle.toHighchartsStyle(axisLabelsStyle),
        useHTML: true,
        align: 'low',
        textAlign: 'left',
        margin: 0,
      },

      scrollbar: {
        enabled: true,
        showFull: false,
        height: 6,
        margin: 4,
      },
    };
  }

  readonly maxDisplayedCategories = computed(() => this.computeMaxDisplayedCategories());
  computeMaxDisplayedCategories() {
    return undefined;
  }

  readonly visibleOrderedCategoriesFromFirstGroupingByValueMap = computed(() => {
    let categories = this.visibleOrderedCategoriesFromFirstGrouping();
    let map = new Map<any, OrderedChartCategory>();

    for (let c of categories) {
      for (let k of c.keys) {
        map.set(k, c);
      }
    }

    return map;
  });
  
  readonly visibleOrderedCategoriesFromFirstGrouping = computed(() => {
    let categories = this.orderedCategoriesFromFirstGrouping();
    let visibleCategories = categories.filter(x => !x.hidden);
    let hiddenCategories = categories.filter(x => x.hidden);

    if (hiddenCategories.length) {
      let othersCategory: OrderedChartCategory = {
        index: visibleCategories.length,
        total: hiddenCategories.reduce((n, x) => n + x.total, 0),
        display: `Others (${hiddenCategories.length})`,
        hidden: false,

        firstKey: undefined,
        isOthers: true,
        keys: new Set(hiddenCategories.flatMap(x => [...x.keys.values()])),
      };

      visibleCategories.push(othersCategory);
    }

    return visibleCategories.map((x, i) => ({
      ...x,
      index: i
    }));
  });

  readonly orderedCategoriesFromFirstGrouping = computed<ReadonlyArray<OrderedChartCategory>>(() => {
    let categories = this.computeOrderedCategoriesFromFirstGrouping();

    let maxDisplayedCategories = this.maxDisplayedCategories();
    if (maxDisplayedCategories && (categories.length - maxDisplayedCategories) > 1) {
      categories = categories.map((x, i) => ({
        ...x,
        hidden: i >= maxDisplayedCategories
      }));
    }

    return categories;
  });

  computeOrderedCategoriesFromFirstGrouping(): OrderedChartCategory[] {
    let categories = [...this.categoriesFromFirstGrouping()];
    let fields = [...this.fields.all()];

    //ComputeRequestFields loses role info so we save the type
    let fieldByRoleType = Object.fromEntries(fields.map(x => [x.visualFieldId, x.role().type]));
    if (!fields.some(f => f.sorted()))
      fields = this.applyDefaultSorting(fields);

    let field = fields.find(f => f.sorted());
    let invert = field.sortDirection() == SortDirection.Ascending ? 1 : -1;

    let orderedCategories: ChartCategory[];

    if (fieldByRoleType[field.visualFieldId] != this.measuresRoleType) {
      let comparer = dataComparer.getComparerForField(field);

      orderedCategories = categories.sort((a, b) => {
        let d = isOthersCompare(a, b);
        if (d != 0)
          return d;

        return comparer(a.firstKey, b.firstKey) * invert;
      });
    }
    else {
      orderedCategories = categories.sort((a, b) => {
        let d = isOthersCompare(a, b);
        if (d != 0)
          return d;

        return dataComparer.numberComparer(a.total, b.total) * invert;
      });
    }

    return orderedCategories.map((x, i) => ({ ...x, index: i, hidden: false }));

    function isOthersCompare(a: any, b: any) {
      if (a.isOthers || b.isOthers) {
        let x = a.isOthers ? 1 : -1;
        let y = b.isOthers ? 1 : -1;
        return (x - y) * invert;
      }

      return 0;
    }
  }


  readonly categoriesFromFirstGrouping = computed<ReadonlyArray<ChartCategory>>(() => {
    if (!this.isVisualValid())
      return null;

    let groupings = this.groupingFields();
    let measures = this.measureFields();

    let rowsResource = this.rows();
    if (!rowsResource?.hasResult())
      return null;

    let result = rowsResource.result();
    let rowsFormatter = this.rowsFormatter();
    
    let grouping = groupings[0];
    let groupingDataIndex = result.columns.get(grouping)?.dataIndex;
    if (isNullOrUndefined(groupingDataIndex))
      return [];

    let rows = result.rows;
    let rawValues = new Set(rows.map(r => r[groupingDataIndex]));

    let map = new Map();
    for (let v of rawValues) {
      map.set(v, {
        key: v,
        display: rowsFormatter.getValueDisplay(grouping, v),
        total: 0
      });
    }

    if (measures.length) {
      let measure = measures[0];
      if (measure.dataType.isNumeric) {
        let measureDataIndex = result.columns.get(measure)?.dataIndex;
        if (!isNullOrUndefined(measureDataIndex)) {
          for (let r of rows) {
            let g = r[groupingDataIndex];
            let m = r[measureDataIndex];
            let category = map.get(g);
            if (category)
              category.total += m;
          }
        }
      }
    }

    let categories: ChartCategory[] = [...map.values()].map(x => ({
      isOthers: false,
      firstKey: x.key,
      keys: new Set([x.key]),
      display: x.display,
      total: x.total,
    }));

    return categories;
  });


  readonly xAxisTitleOffset = signal('0px');
  readonly xAxisTitleEnabled = signal(false);

  readonly xAxisTitle = computed(() => {
    let groupings = this.groupingFields();
    let legends   = this.legendFields();
    let measures  = this.measureFields();

    let labelField =
      (groupings.length > 0) ? groupings[0].shortTitle :
      (legends.length > 0)   ? legends[0].shortTitle   :
      (measures.length > 0)  ? measures[0].shortTitle  : '';

    let axisLabelsStyle = this.style()?.axisLabels();

    return axisLabelsStyle?.enabled ? labelField : '';
  });

  readonly xAxisTitleStyle = computed(() => {
    let axisLabelsStyle = this.style()?.axisLabels();
    let offsetType = this.isHorizontalPlot() ? 'left' : 'bottom';

    return [
      TextStyle.toCssVariables(axisLabelsStyle),
      `${offsetType}: ${this.xAxisTitleOffset()};`
    ].join('\n');
  });

  readonly yAxisStyle = computed(() => this.style()?.yAxis());
  readonly yAxis = computed<YAxisOptions>(() => this.computeYAxis());

  computeYAxis(): YAxisOptions {
    if (!this.isVisualValid())
      return null;

    let measures = this.measureFields();

    let seriesNames = [];
    measures.forEach((m, i) => seriesNames.push(m.shortTitle));

    let seriesText = '';
    if (seriesNames.length == 1)
      seriesText = seriesNames[0];
    else if (seriesNames.length > 1) {
      let right = seriesNames.splice(seriesNames.length - 1, 1);
      let left = seriesNames;
      seriesText = `${left.join(', ')} and ${right[0]}`;
    }

    let axisLabelsStyle = this.style()?.axisLabels();
    let yAxisStyle = this.style()?.yAxis();
    
    return {
      labels: {
        enabled: !!yAxisStyle?.enabled,
        useHTML: false,
        style: TextStyle.toHighchartsStyle(yAxisStyle),
        allowOverlap: true,
        reserveSpace: true,
      },
      reversedStacks: false,
      title: {
        text: axisLabelsStyle?.enabled ? seriesText : '',
        style: TextStyle.toHighchartsStyle(this.style()?.axisLabels()),
      },
    };
  }


  readonly series = computed<ChartSeries<TSeries> | ChartSeries<TSeries>[]>(() => this.computeSeries());

  computeSeries(): ChartSeries<TSeries> | ChartSeries<TSeries>[] {
    if (!this.isVisualValid())
      return null;

    let measures = this.measureFields();
    let groupings = this.groupingFields();
    let legends = this.legends();

    let rowsResource = this.rows();
    if (!rowsResource?.hasResult())
      return undefined;

    let createSeriesOptions = new CreateSeriesOptions(this.rowsResult(), this.rowsFormatter());

    groupings?.forEach((g, i) => createSeriesOptions.addGrouping(i, g));
    measures?.forEach((m, i) => createSeriesOptions.addMeasure(i, m));
    legends?.forEach((l, i) => createSeriesOptions.addLegend(i, l));

    let series = this._createAllSeries(createSeriesOptions);

    return series;
  }

  useSingleSeriesStack(): boolean {
    return false;
  }

  private _nextSeriesIndex = 0;

  protected resetSeriesIndex() {
    this._nextSeriesIndex = 0;
  }

  protected getNextSeriesIndex() {
    return this._nextSeriesIndex++;
  }

  protected getColorByIndex(index: number) {
    return HighchartsChart.defaultColors[index % HighchartsChart.defaultColors.length];
  }


  private _createAllSeries(o: CreateSeriesOptions): ChartSeries<TSeries> | ChartSeries<TSeries>[] {
    this.resetSeriesIndex();
    return this.createAllSeries(o);
  }

  createAllSeries(o: CreateSeriesOptions): ChartSeries<TSeries> | ChartSeries<TSeries>[] {
    if (o.legends?.length) {
      return o.legends.flatMap(l => l.legend.items.flatMap(i => {
        return o.measures.map(m => this.createMeasureSeries(new CreateSeries(o, m, i.rows, l, i)));
      }));
    }
    else {
      return o.measures.map(m => this.createMeasureSeries(new CreateSeries(o, m, o.result.rows)));
    }
  }

  createMeasureSeries(series: CreateSeries): ChartSeries<TSeries> {
    let [o, m, l] = [
      series.options,
      series.measure,
      series.legendItem
    ];

    let seriesName = l?.display ?? m.field.shortTitle;
    let seriesId = `_${ChartVisual.getNextSeriesId()}`;

    let seriesIndex = this.getNextSeriesIndex();

    let createSeriesPoint = (row: Values): ChartPoint => {
      return this.createSeriesPoint({
        series,
        row,
        colorIndex: seriesIndex
      });
    };

    let s: ChartSeries<TSeries> = <any>{
      id: seriesId,
      name: seriesName,
      data: series.rows.map(createSeriesPoint).filter(x => !!x),
      color: HighchartsChart.getColorByIndex(seriesIndex),
      marker: {symbol: HighchartsChart.getSymbolByIndex(seriesIndex)},
      dataGrouping: { enabled: false },
      dataLabels: this.dataLabels(),
    };

    if (this.useSingleSeriesStack()) {
      s.stack = 0;
    }
    else {
      if (l) {
        s.stack = l.index;
      }
    }

    return s;
  }

  createSeriesPoint(point: CreatePoint): ChartPoint {
    let [o, m, l, r] = [
      point.series.options,
      point.series.measure,
      point.series.legend,
      point.row
    ];

    let valueDisplay = o.formatter.getValueDisplay(m.field, r[m.dataIndex]);
    let groupingDisplay = o.allGroupingsDisplay(r);
    let groupingData = o.allGroupingsData(r);
    let groupingDisplayString = groupingDisplay.join(', ');

    let parent: TooltipParentData = point.series.legendItem ? {
      title: point.series.legendItem.legend.field.shortTitle,
      display: point.series.legendItem.display
    } : null;

    let value = o.measureData(m.index, r);
    
    let dataPointFields: DataPointField[] = [];

    o.groupings?.forEach(x => dataPointFields.push({ field: x.field, value: r[x.dataIndex] }));

    if (l)
      dataPointFields.push({ field: l.field, value: r[l.dataIndex] });

    if (m)
      dataPointFields.push({ field: m.field, value: r[m.dataIndex] });

    let dataPoint = this.dataPoints.get(dataPointFields);

    let categoryMap = this.visibleOrderedCategoriesFromFirstGroupingByValueMap();
    let category = categoryMap.get(groupingData[0]);

    if (category && category.hidden)
      return undefined;

    let p: ChartPoint = {
      name: groupingDisplayString,
      label: groupingDisplayString,
      x: category?.index,
      y: value,
      custom: {
        ...this.createSeriesPointCustomData(
          dataPoint,
          groupingDisplayString,
          o.groupings,
          groupingDisplay,
          m.field,
          valueDisplay,
          parent),
          groupingData
      },
      color: HighchartsChart.getColorByIndex(point.colorIndex),
    };

    return p;
  }
  
  createSeriesPointCustomData(
    dataPoint: DataPoint,
    groupingKey: any,
    groupings: ReadonlyArray<SeriesField>,
    groupingsDisplay: ReadonlyArray<string>,
    measure: VisualField,
    valueDisplay: string | (() => string),
    parent?: TooltipParentData
  ): any {
    return <TooltipFormatArgs>{
      groupings: groupings.map(g => g.field),
      groupingsDisplay,

      measure: measure,
      valueDisplay,

      shared: this.sharedTooltips(),
      dontReuse: false,

      parent,

      tooltip: null,
      groupingKey,

      dataPoint
    };
  }


  readonly tooltip = computed<Highcharts.TooltipOptions>(() => this.computeTooltipOptions());

  private readonly tooltipsVersion = signal(1);
  private readonly updateTooltipsVersion = effect(() => {
    let fields = [...this.fields.all()];
    // Depends on shortTitle
    let _ = fields.forEach(f => f.shortTitle);
   
    this.tooltipsVersion.update(x => x + 1);
  });

  computeTooltipOptions(): Partial<Highcharts.TooltipOptions> {
    if (!this.isVisualValid())
      return null;

    let self = this;
    let tooltipsVersion = this.tooltipsVersion();

    return {
      useHTML: true,
      shadow: false,
      backgroundColor: null,
      borderWidth: 0,
      outside: true,
      followTouchMove: false,

      split: false,
      shared: this.sharedTooltips(),
      stickOnContact: false,
      followPointer: true,
      fixed: false,

      distance: 0,
      animation: { duration: 200 },
      hideDelay: 1,

      formatter: function (this: Highcharts.Point, tooltip: Highcharts.Tooltip) {
        if (self.useDataTooltip) {
          let hoveredPoint = self.chart()?.chart()?.hoverPoint;

          let dataTooltip = self.createDataTooltipFromHighcharts(this, hoveredPoint);
          if (!dataTooltip)
            return undefined;

          return self.setDataTooltip(dataTooltip);
        }

        let custom = <TooltipFormatArgs>this.options.custom;
        if (!custom || !custom.measure || !custom.valueDisplay) {
          return undefined;
        }
        else {
          let args = <TooltipCustomData>custom;
          if (args.dontReuse)
            this.options.custom = { ...args };

          args.p = this;

          args.points = (args.shared && this.points) ? this.points?.map(p => {
            if ('custom' in p) {
              return {
                ...(<any>p).custom,
                p
              };
            }

            return null;
          }).filter(x => !!x) : [args];

          if (!args.tooltip || args.tooltipVersion != tooltipsVersion) {
            args.tooltipVersion = tooltipsVersion;
            args.tooltip = self.formatTooltipBodyHtml(args);
          }

          return args.tooltip;
        }
      },
    };
  }

  readonly sharedTooltips = computed(() => {
    return this.computeSharedTooltips();
  });

  computeSharedTooltips() {
    return false;
  }

  maxTooltipRows() {
    return 9;
  }

  formatTooltipBodyHtml(args: TooltipCustomData): string {
    let html = [`
      <div class="pbir-visual-data-tooltip">
        <div class="pbir-visual-data-tooltip__rows">`];

    let headerItems = [];

    args.groupings.forEach((g, i) => headerItems.push({
      title: g.shortTitle,
      display: args.groupingsDisplay[i]
    }));

    if (args.shared) {
      if (args.parent) {
        headerItems.push({
          title: args.parent.title,
          display: args.measure.shortTitle
        });
      }
    }
    else {
      if (args.parent) {
        headerItems.push({
          title: args.parent.title,
          display: args.parent.display
        });
      }
    }


    for (let i = 0; i < headerItems.length; ++i) {
      let last = i == headerItems.length - 1;
      let item = headerItems[i];

      let className = last ? 'pbir-visual-data-tooltip__row-header-last' : '';

      html.push(`
        <div class="pbir-visual-data-tooltip__row pbir-visual-data-tooltip__row-header ${className}">
        <span class="pbir-visual-data-tooltip__row-title">
          <span class="pbir-visual-data-tooltip__row-text">${item.title}</span>
        </span>
        <span class="pbir-visual-data-tooltip__row-value">${item.display}</span>
        </div>`);
    }

    let rows = [];
    
    for (let i = 0; i < args.points.length; ++i) {
      rows.push({
        color: args.points[i].p.color,
        title: this.formatTooltipMeasureTitle(args, i),
        value: this.formatTooltipMeasureValueText(args, i),
        raw: args.points[i].p.y ?? args.points[i].p.key,
      });
    }

    rows.sort((a, b) => b.raw - a.raw);

    
    let max = Math.min(this.maxTooltipRows(), rows.length);
    let remainingCount = Math.max(rows.length - max, 0);

    for (let i = 0; i < max; ++i) {
      let r = rows[i];

      html.push(`
        <div class="pbir-visual-data-tooltip__row">
          <span class="pbir-visual-data-tooltip__row-title">
            <span class="pbir-visual-data-tooltip__row-color" style="background: ${r.color}"></span>
            <span class="pbir-visual-data-tooltip__row-text">${r.title}</span>
          </span>
          <span class="pbir-visual-data-tooltip__row-value">${r.value}</span>
        </div>`);
    }

    if (remainingCount > 0) {
      html.push(`
        <div class="pbir-visual-data-tooltip__row">
          <span class="pbir-visual-data-tooltip__row-title">
            <span class="pbir-visual-data-tooltip__row-color"></span>
            <span class="pbir-visual-data-tooltip__row-text"></span>
          </span>
          <span class="pbir-visual-data-tooltip__row-value">${remainingCount} more...</span>
        </div>`);
    }

    html.push(`</div></div>`);

    return html.join('');
  }

  formatTooltipMeasureTitle(args: TooltipCustomData, index: number) {
    let p = args.points[index];

    if (args.shared && p.parent) {
      return p.parent.display;
    }

    let pointMeasure = (p as any).measure as VisualField;
    if (pointMeasure) {
      return pointMeasure.shortTitle;
    }

    return args.measure.shortTitle;
  }

  formatTooltipMeasureValueText(args: TooltipCustomData, index: number) {
    let p = args.points[index];
    let valueDisplay = typeof p.valueDisplay === 'function' ? p.valueDisplay() : p.valueDisplay;

    if (this.tooltipsIncludePercentage() && p.p.percentage)
      return `${valueDisplay} (${formatPercentage(p.p.percentage)}%)`;

    return valueDisplay;
  }

  tooltipsIncludePercentage() {
    return false;
  }


  readonly legendStyle = computed(() => this.style()?.legend());
  readonly legendOptions = computed<Highcharts.LegendOptions>(() => this.computeLegend());

  readonly legendAlignment = computed<ChartVisual.Alignment | null>(() => {
    let legendStyle = this.legendStyle();
    if (legendStyle?.enabled) {
      let alignment = legendStyle.position?.alignment;
      if (alignment)
        return alignment;
    }

    return null;
  });

  computeLegend(): Partial<Highcharts.LegendOptions> {
    if (CUSTOM_LEGEND) {
      return {
        enabled: false,
      };
    }

    let legendStyle = this.legendStyle();

    let margin = this.getLegendMargin();

    let lineHeight = FontSize.value(legendStyle?.fontSize, 12);

    return {
      ...legendStyle?.position?.legendOptions,

      enabled: legendStyle?.enabled,
      useHTML: true,
      itemStyle: TextStyle.toHighchartsStyle(legendStyle),
      margin:
        (this.isHorizontalPlot()
          ? (legendStyle?.position?.alignment == ChartVisual.Alignment.Right)
          : (legendStyle?.position?.alignment == ChartVisual.Alignment.Top))
          ? Math.max(margin, this.getSpacingForDataLabels())
          : margin,

      symbolHeight: lineHeight + 1,
      itemMarginBottom: 0,
      itemMarginTop: 0,

      events: {
        itemClick: function (this: Highcharts.Legend, event: Highcharts.LegendItemClickEventObject) {
          let visible = (event.legendItem as any).visible;
          if (visible && !this.allItems.some(x => x != event.legendItem && x.visible))
            event.preventDefault();
        },
      }
    };
  }

  getLegendMargin() {
    return 10;
  }


  readonly dataLabelsStyle = computed(() => {
    let dataLabels = this.style()?.dataLabels();
    if (dataLabels) {
      if (dataLabels.numberFormat == ChartVisual.NumberFormat.Percentage
        && !this.visual()?.percentageDisplayChart()) {
        dataLabels = {
          ...dataLabels,
          numberFormat: ChartVisual.NumberFormat.Default
        };
      }

      dataLabels = {
        ...dataLabels,
        fontSize: dataLabels.fontSize ?? { size: 12, kind: 'px' }
      };
    }

    return dataLabels;
  });

  readonly dataLabels = computed<Highcharts.DataLabelsOptions>(() => this.computeDataLabels());

  computeDataLabels(): Partial<Highcharts.DataLabelsOptions> {
    let options = this.options() as ChartVisual.Options;
    if (options?.noDataLabels)
      return { enabled: false };

    return this.createDataLabelsOptions();
  }

  readonly createDataLabelsOptions = computed<Partial<Highcharts.DataLabelsOptions>>(() => {
    let options = this.options() as ChartVisual.Options;

    let dataLabelsStyle = this.dataLabelsStyle();
    let dataLabelsFormatter = this.dataLabelPointFormatter();

    return {
      ...options.dataLabelPosition?.dataLabelsOptions,
      overflow: 'allow',
      crop: false,
      allowOverlap: true,

      enabled: !!dataLabelsStyle?.enabled,
      defer: false,
      animation: false,

      useHTML: false,
      className: 'pbir-visual__chart-data-label',

      backgroundColor: dataLabelsStyle?.backgroundColor,
      
      style: {
        ...TextStyle.toHighchartsStyle(dataLabelsStyle),
        textOutline: 'none',
      },

      borderColor: dataLabelsStyle?.borderColor,
      borderWidth: 0.5,

      padding: 5,
      inside: this.areDataLabelsInside(),

      zIndex: 100,

      formatter: function (options) {
        return dataLabelsFormatter(this, options);
      },

      nullFormatter: function (options) {
        return '#NULL';
      },
    };
  });

  readonly dataLabelPointFormatter = computed(() => this.getDataLabelPointFormatter());

  getDataLabelPointFormatter(): (point: Highcharts.Point, options: Highcharts.DataLabelsOptions) => string {
    let dataLabelsStyle = this.dataLabelsStyle();

    let numberFormat =
      ChartVisual.NumberFormat.options.find(x => x.id == dataLabelsStyle?.numberFormat)
        ?? ChartVisual.NumberFormat.options.find(x => x.isDefault);

    let formatter = this.formatter();
    let showPercentageOfTotal = this.showPercentageOfTotalInDataLabel();
    let showName = this.showNameInDataLabel();

    return (point, options) => {
      let formattedValue = '';

      let pointValue =
        'value' in point ? point.value :
        'y'     in point ? point.y     : undefined;

      let custom = (point as any).custom as TooltipCustomData;
      if (custom) {
        let formatted = false;

        let field: VisualField;
        let value = pointValue;
        let formatArgs: DisplayFormatArgs = {};

        if ('dataLabel' in custom) {
          let dataLabel = custom.dataLabel as DataLabelData;
          if ('text' in dataLabel) {
            formattedValue = dataLabel.text;
            formatted = true;
          }
          else {
            field = dataLabel.field;
            value = dataLabel.value;
            formatArgs = {
              ...formatArgs,
              ...dataLabel.displayFormatArgs
            };
          }
        }
        else {
          field = custom.measure;
        }

        if (!formatted) {
          if (field.dataType.isNumeric && numberFormat.configureDisplay) {
            formatArgs = {
              ...formatArgs,
              display: numberFormat.configureDisplay(field.dataDisplay)
            };

            formattedValue = formatter.getValueDisplay(field, value, formatArgs);
          }
          else {
            let valueDisplay =
              custom.valueDisplay
                ? custom.valueDisplay
                : (custom.points?.length ? custom.points[0].valueDisplay : null);

            if (valueDisplay) {
              valueDisplay = custom.valueDisplay;
              valueDisplay = typeof valueDisplay === 'function' ? valueDisplay() : valueDisplay;
              formattedValue = valueDisplay;
            }
            else {
              formattedValue = formatter.getValueDisplay(field, value, formatArgs);
            }
          }
        }
      }
      else {
        return '' + point.name;
      }

      if (showName)
        formattedValue = `${point.name} — ${formattedValue}`;

      if (showPercentageOfTotal) {
        let percentage = `${formatPercentage(point.percentage)}%`;
        formattedValue = `${formattedValue} (${percentage})`;
      }

      return formattedValue;
    };
  }
  
  readonly showPercentageOfTotalInDataLabel = computed(() => this.computeShowPercentageOfTotalInDataLabel());
  readonly showNameInDataLabel = computed(() => this.computeShowNameInDataLabel());

  computeShowPercentageOfTotalInDataLabel() { return false; }
  computeShowNameInDataLabel() { return false; }

  isHorizontalPlot() {
    return false;
  }

  areDataLabelsInside() {
    return false;
  }


  readonly navigatorOptions = computed(() => this.computeNavigatorOptions());

  computeNavigatorOptions(): Highcharts.NavigatorOptions {
    return {
    };
  }


  readonly colorAxisOptions = computed(() => this.computeColorAxisOptions());
  
  computeColorAxisOptions(): Highcharts.ColorAxisOptions | Highcharts.ColorAxisOptions[] {
    if (!this.useColorAxis)
      return undefined;

    return HighchartsChart.defaultColors.map(x => <Highcharts.ColorAxisOptions>{
      type: 'logarithmic',
      minColor: '#ffffff',
      maxColor: x,
    });
  }

  get useColorAxis() {
    return false;
  }


  readonly legendFontSize = computed(() => {
    let value = this.legendStyle()?.fontSize ?? { value: 12, kind: 'px' };
    return VisualStyle.fontSizeToCss(value);
  });

  readonly legendTextColor = computed(() => {
    let value = this.legendStyle()?.color ?? '#2e2e2e';
    return value;
  });

  readonly legendScrollElement = viewChild<ElementRef<HTMLElement>>('legendScroll');
  readonly legendItemsElement = viewChild<ElementRef<HTMLElement>>('legendItems');

  readonly customLegend = new CustomLegend(this);

  readonly customLegendItems = computed(() => {
    let _ = this.customLegend.redraw();
    return this.computeCustomLegendItems();
  });


  readonly groupingValues = computed(() => this.computeGroupingValues());

  protected computeGroupingValues() {
    if (!this.groupingFields()?.length)
      return [];

    let result = this.rowsResult();
    let rows = result?.rows;
    if (!rows)
      return [];

    let groupingField = this.groupingFields()[0];
    let dataIndex = result.columns.get(groupingField).dataIndex;
    let groupingValues = rows.map(x => x[dataIndex]);
    let values = distinctValues(groupingValues);
    return values;
  }


  protected computeCustomLegendItems(): ReadonlyArray<CustomLegendItem> {
    let hc = this.chart()?.chart();
    if (!hc)
      return [];

    let items: CustomLegendItem[] = [];

    let mode = this.customLegendMode();

    if (mode == CustomLegendMode.PerPoint) {
      hc.series?.forEach(
        x => x.points?.forEach(
          p => items.push(new CustomLegendItem(new HighchartPointCustomLegendTarget(p)))));
    }
    else if (mode == CustomLegendMode.PerSeries) {
      hc.series?.forEach(
        x => items.push(new CustomLegendItem(new HighchartSeriesCustomLegendTarget(x))));
    }
    else if (mode == CustomLegendMode.PerGrouping) {
      // if no grouping/legend fields are present, we will use measure name for category
      let hasGrouping = this.groupingFields().length || this.legendFields().length;

      // in per grouping mode, we find all points for specific group and create legend items that manage them all
      let pointsByGrouping = new Map<any, Highcharts.Point[]>();

      hc.series?.forEach(
        x => x.points?.forEach(p => {
          let customData = (p as any).custom as TooltipCustomData;
          let grouping = customData.groupingKey;
          let groupingPoints = pointsByGrouping.get(grouping);
          if (!groupingPoints) {
            groupingPoints = [];
            pointsByGrouping.set(grouping, groupingPoints);
          }

          groupingPoints.push(p);
        }));

      for (let [k, v] of pointsByGrouping) {
        let customData = (v[0] as any).custom as TooltipCustomData;
        let displayName = hasGrouping ? customData.groupingsDisplay.join(', ') : customData.measure.shortTitle;
        items.push(new CustomLegendItem(new HighchartPointsCustomLegendTarget(v, displayName)));
      }
    }

    items.sort((a, b) => a.target.getName().localeCompare(b.target.getName()));

    return items;
  }

  customLegendMode(): CustomLegendMode {
    return CustomLegendMode.PerSeries;
  }


  readonly wrapperClasses = computed(() => this.computeWrapperClasses());

  protected computeWrapperClasses() {
    let classes = [];

    if (this.customLegend.enabled()) {
      classes.push('has-legend');
      classes.push(this.customLegend.className());

      if (this.customLegend.needsNavHorizontally()) {
        classes.push('legend__scroll-h');
      }
      else if (this.customLegend.needsNavVertically()) {
        classes.push('legend__scroll-v');
      }
    }

    if (this.includeHasSelectionClass()) {
      classes.push('has-selection');
    }

    classes.push(`pbir-chart-${this.type.className}`);

    return classes.join(' ');
  }

  readonly wrapper: Signal<ElementRef<HTMLElement>> = viewChild('wrapper', { read: ElementRef });

  private applyWrapperHasSelectionClass() {
    let wrapper = this.wrapper()?.nativeElement as HTMLElement;
    wrapper?.classList.toggle('has-selection', this.includeHasSelectionClass());
  }

  protected includeHasSelectionClass() {
    return this.hasSelection() || this.pointsSelected();
  }


  protected onChartRedraw(chart: Chart) {
  }

  protected onChartRender() {
    this.customLegend.redraw.update(x => x + 1);
    this.overrideXAxisTitle();
  }

  protected overrideXAxisTitle() {
    this.xAxisTitleEnabled.set(false);

    let options = this.type.visualOptions as ChartVisual.Options;
    if (!options?.noXAxis && this.xAxisStyle()?.enabled) {
      let hc = this.chart()?.chart();
      if (hc) {
        let positionElement = (hc.xAxis[0] as any).axisTitle?.element as HTMLElement;
        if (positionElement) {
          let horizontal = this.isHorizontalPlot();
          let relativeTo = this.wrapper().nativeElement.getBoundingClientRect();
          let offsetRect = positionElement.getBoundingClientRect();

          let offset = horizontal ? (offsetRect.left - relativeTo.left) : (relativeTo.bottom - offsetRect.bottom);
          this.xAxisTitleOffset.set(`${offset}px`);
          this.xAxisTitleEnabled.set(true);
        }
      }
    }
  }



  override beforeFormatValue(value: any) {
    return value;
  }

  override afterFormatValue(value: any) {
    return value && BigNumber.isBigNumber(value) ? value.toNumber() : value;
  }

  
  protected override getClickEventData(e: MouseEvent): VisualHost.ClickEventData {
    let data = super.getClickEventData(e);

    let target = e.target as HTMLElement;
    if (target.classList.contains('highcharts-scrolling')) {
      data = {
        ...data,
        resetCrossFilter: false
      };

      return data;
    }

    if (target.closest('.highcharts-series') ||
        target.closest('.highcharts-markers') ||
        target.closest('.highcharts-point')) {

      data = {
        ...data,
        action: VisualHost.ClickAction.Selection,
        resetCrossFilter: this.isOtherVisualCrossFilterSource()
      };
    }

    return data;
  }


  readonly dataTooltipTemplate = viewChild<TemplateRef<any>>('dataTooltipTemplate');
  readonly viewContainer = inject(ViewContainerRef);

  get useDataTooltip() {
    return true;
  }
  
  createDataTooltipFromHighcharts(tooltipPoint: Highcharts.Point, hoveredPoint: Highcharts.Point): DataTooltip {
    let targetCustom = (tooltipPoint as any).custom as DataTooltipPointCustomData;
    if (!targetCustom)
      return this.createDataTooltipFromHighchartsLegacy(tooltipPoint);
      
    if ('dataTooltip' in targetCustom)
      return targetCustom.dataTooltip;

    if (!targetCustom || !('dataPoint' in targetCustom))
      return this.createDataTooltipFromHighchartsLegacy(tooltipPoint);

    let headers: DataTooltipHeader[] = [];
    let columns: DataTooltipColumn[] = [];
    let rows: (DataTooltipRow & { sortKey: any, point: Highcharts.Point })[] = [];

    let groupingFields = this.groupingFields();
    let legendFields = this.legendFields();
    let measureFields = this.measureFields();

    let fieldPoints = new Map<VisualField, { p: Point; dp: DataPoint }>();

    let points = tooltipPoint.points?.length ? tooltipPoint.points : [tooltipPoint];
    for (let p of points) {
      let custom = (p as any).custom as DataTooltipPointCustomData;
      if (!custom || !('dataPoint' in custom))
        continue;

      let dataPoint = custom.dataPoint;
      for (let dp = dataPoint; dp.parent; dp = dp.parent) {
        fieldPoints.set(dp.field, { p, dp });
      }

      if (legendFields.length) {
        let dp = dataPoint.parent;
        let measureValues = measureFields.map(m => {
          let children = dp.getChildrenByField(m);
          if (!children)
            return null;

          return {
            dp: children[0],
            p: fieldPoints.get(m).p
          };
        }).filter(x => x);

        if (measureValues.length) {
          let style = (p == hoveredPoint) ? DataTooltipRowStyle.Emphasis : DataTooltipRowStyle.Default;

          rows.push({
            color: '' + p.color,
            name: dp.valueDisplay,
            values: measureValues.map(m => this.dataTooltipFormatRowValue(m.p, m.dp)),
            style,
            sortKey: measureValues[0].dp.value,
            point: p,
          });
        }
      }
    }
    
    for (let f of groupingFields) {
      headers.push({
        name: f.shortTitle,
        value: fieldPoints.get(f).dp.valueDisplay,
      });
    }

    if (legendFields.length) {
      for (let f of legendFields) {
        if (!fieldPoints.has(f))
          continue;

        let { p, dp } = fieldPoints.get(f);
        columns.push({
          name: f.shortTitle,
          values: measureFields.map(x => x.shortTitle),
        });
      }
    }
    else {
      for (let mf of measureFields) {
        if (!fieldPoints.has(mf))
          continue;

        let { p, dp } = fieldPoints.get(mf);
        let style = (p == hoveredPoint) ? DataTooltipRowStyle.Emphasis : DataTooltipRowStyle.Default;
        rows.push({
          color: '' + p.color,
          name: dp.field.shortTitle,
          values: [this.dataTooltipFormatRowValue(p, dp)],
          style,
          sortKey: dp.value,
          point: p
        });
      }
    }

    if (hoveredPoint) {
      rows.sort((a, b) => {
        let d = ((b.point == hoveredPoint) ? 1 : 0) - ((a.point == hoveredPoint) ? 1 : 0);
        if (d != 0)
          return d;

        return b.sortKey - a.sortKey;
      });
    }
    else {
      rows.sort((a, b) => b.sortKey - a.sortKey);
    }

    return this.normalizeDataTooltip({
      headers,
      columns,
      rows,
    });
  }

  protected normalizeDataTooltip(tooltip: DataTooltip): DataTooltip {
    let { headers, columns, rows } = tooltip;

    let max = Math.min(this.maxTooltipRows(), rows.length);
    let remainingRowCount = Math.max(rows.length - max, 0);

    if (rows.length > max)
      rows = rows.slice(0, max);

    return {
      headers,
      columns,
      rows,
      remainingRowCount
    };
  }


  dataTooltipFormatRowValue(p: Highcharts.Point, dp: DataPoint) {
    let valueDisplay = dp.valueDisplay;

    if (this.tooltipsIncludePercentage() && p.percentage)
      return `${valueDisplay} (${formatPercentage(p.percentage)}%)`;

    return valueDisplay;
  }

  createDataTooltipFromHighchartsLegacy(p: Highcharts.Point): DataTooltip {
    let args = <TooltipCustomData>(p as any).custom;
    if (!args)
      return null;

    args.p      = p;
    args.points = (args.shared && p.points) ? p.points?.map(p => {
      if ('custom' in p) {
        return {
          ...(<any>p).custom,
          p
        };
      }

      return null;
    }).filter(x => !!x) : [args];
    
    let headers: DataTooltipHeader[] = [];
    let columns: DataTooltipColumn[] = [];
    let rows: (DataTooltipRow & { raw: any })[] = [];

    args.groupings.forEach((g, i) => headers.push({
      name: g.shortTitle,
      value: args.groupingsDisplay[i]
    }));
    
    if (args.shared) {
      if (args.parent) {
        columns.push({
          name: args.parent.title,
          values: [args.measure.shortTitle]
        });
      }
    }
    else {
      if (args.parent) {
        columns.push({
          name: args.parent.title,
          values: [args.parent.display]
        });
      }
    }
    
    for (let i = 0; i < args.points.length; ++i) {
      rows.push({
        color: '' + args.points[i].p.color,
        name: this.formatTooltipMeasureTitle(args, i),
        values: [this.formatTooltipMeasureValueText(args, i)],
        raw: args.points[i].p.y ?? args.points[i].p.key,
      });
    }

    rows.sort((a, b) => b.raw - a.raw);

    let max = Math.min(this.maxTooltipRows(), rows.length);
    let remainingRowCount = Math.max(rows.length - max, 0);

    return {
      headers,
      columns,
      rows,
      remainingRowCount
    };
  }


  private _datatooltipView: EmbeddedViewRef<any>;

  setDataTooltip(dataTooltip: DataTooltip) {
    this._datatooltipView?.destroy();

    this._datatooltipView = this.dataTooltipTemplate().createEmbeddedView({ dataTooltip });
    this._datatooltipView.detectChanges();

    let parentKey = `pbir-datatooltip-parent-${nextDataTooltipParentId++}`;

    let parent = document.createElement('span');
    parent.classList.add(parentKey);

    timer(0).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(() => {
        let parent = document.querySelector(`.${parentKey}`);
        parent?.parentElement.style.setProperty('display', 'block', 'important');
        parent?.replaceWith(...this._datatooltipView.rootNodes);
      })
    ).subscribe();

    return parent.outerHTML;
  }
}

let nextDataTooltipParentId = 1;

export interface ChartCategory {
  readonly isOthers?: boolean;
  readonly firstKey: any;
  readonly keys: ReadonlySet<any>;
  readonly display: string;
  readonly total: number;
}

export type OrderedChartCategory = ChartCategory & {
  readonly index: number;
  readonly hidden: boolean;
}

export namespace ChartVisual {
  let nextSeriesId = 1;

  export function getNextSeriesId() { 
    return `_${nextSeriesId++}`; 
  }


  export class State extends Visual.State {
  }

  //#region Style
  export enum Alignment {
    Left,
    Top,
    Right,
    Bottom
  }


  export interface NumberFormatConfig {
    readonly id: NumberFormat;
    readonly title: string;

    readonly isDefault?: boolean;
    readonly configureDisplay?: (display: DataDisplayOptions) => DataDisplayOptions;
  }

  export enum NumberFormat {
    Default,
    Compact,
    CompactNoDecimals,
    Percentage,
  }

  export namespace NumberFormat {
    export const options: ReadonlyArray<NumberFormatConfig> = [
      {
        id: NumberFormat.Default,
        title: 'Full Number',
        isDefault: true,
      },
      {
        id: NumberFormat.Compact,
        title: 'Compact',

        configureDisplay: display => ({
          ...display,
          numberRounding: { method: NumberRoundingMethod.Truncate, precision: 1 },
          significantDigitsOnly: true,
        })
      },
      {
        id: NumberFormat.CompactNoDecimals,
        title: 'Compact - No Decimals',
        
        configureDisplay: display => ({
          ...display,
          numberRounding: { method: NumberRoundingMethod.Truncate, precision: 0 },
          significantDigitsOnly: true,
        })
      },
      {
        id: NumberFormat.Percentage,
        title: 'Percentage',

        configureDisplay: display => ({
          ...display,
          numberFormat: DataNumberFormat.Percentage,
          numberRounding: { method: NumberRoundingMethod.Truncate, precision: 0 },
        })
      },
    ];
  }


  export interface LegendPosition {
    readonly id: string | null;
    readonly title: string;
    readonly legendOptions: Partial<Highcharts.LegendOptions>;
    readonly alignment: Alignment;
    readonly className: string;
  }

  export namespace LegendPosition {
    export const Top: LegendPosition = {
      id: 't',
      title: 'Top',
      legendOptions: {
        align: 'center',
        verticalAlign: 'top',
        layout: 'horizontal',
      },
      alignment: Alignment.Top,
      className: 'legend__top',
    };

    export const TopLeft: LegendPosition = {
      id: 'tl',
      title: 'Top Left',
      legendOptions: {
        align: 'left',
        verticalAlign: 'top',
        layout: 'horizontal',
      },
      alignment: Alignment.Top,
      className: 'legend__top-left',
    };

    export const TopRight: LegendPosition = {
      id: 'tr',
      title: 'Top Right',
      legendOptions: {
        align: 'right',
        verticalAlign: 'top',
        layout: 'horizontal',
      },
      alignment: Alignment.Top,
      className: 'legend__top-right',
    };


    export const Bottom: LegendPosition = {
      id: 'b',
      title: 'Bottom',
      legendOptions: {
        align: 'center',
        verticalAlign: 'bottom',
        layout: 'horizontal',
      },
      alignment: Alignment.Bottom,
      className: 'legend__bottom',
    };

    export const BottomLeft: LegendPosition = {
      id: 'bl',
      title: 'Bottom Left',
      legendOptions: {
        align: 'left',
        verticalAlign: 'bottom',
        layout: 'horizontal',
      },
      alignment: Alignment.Bottom,
      className: 'legend__bottom-left',
    };

    export const BottomRight: LegendPosition = {
      id: 'br',
      title: 'Bottom Right',
      legendOptions: {
        align: 'right',
        verticalAlign: 'bottom',
        layout: 'horizontal',
      },
      alignment: Alignment.Bottom,
      className: 'legend__bottom-right',
    };


    export const Left: LegendPosition = {
      id: 'l',
      title: 'Left',
      legendOptions: {
        align: 'left',
        verticalAlign: 'middle',
        layout: 'vertical',
      },
      alignment: Alignment.Left,
      className: 'legend__left',
    };

    export const Right: LegendPosition = {
      id: 'r',
      title: 'Right',
      legendOptions: {
        align: 'right',
        verticalAlign: 'middle',
        layout: 'vertical',
      },
      alignment: Alignment.Right,
      className: 'legend__right',
    };


    export const options = [
      Bottom,
      BottomLeft,
      BottomRight,
      Top,
      TopLeft,
      TopRight,
      Left,
      Right,
    ];


    export const leftSide = [
      ChartVisual.LegendPosition.Left,
      ChartVisual.LegendPosition.BottomLeft,
      ChartVisual.LegendPosition.TopLeft
    ];

    export const rightSide = [
      ChartVisual.LegendPosition.Right,
      ChartVisual.LegendPosition.BottomRight,
      ChartVisual.LegendPosition.TopRight
    ];

    export const topSide = [
      ChartVisual.LegendPosition.Top,
      ChartVisual.LegendPosition.TopLeft,
      ChartVisual.LegendPosition.TopRight
    ];

    export const bottomSide = [
      ChartVisual.LegendPosition.Bottom,
      ChartVisual.LegendPosition.BottomLeft,
      ChartVisual.LegendPosition.BottomRight
    ];
  }


  export interface DataLabelPosition {
    readonly id: string | null;
    readonly title: string;
    readonly dataLabelsOptions: (isHorizontal: boolean) => Partial<Highcharts.DataLabelsOptions>;
  }

  export namespace DataLabelsPosition {
    const labelOffset = 7;

    export const Middle: DataLabelPosition = {
      id: 'm',
      title: 'Middle',
      dataLabelsOptions: isHorizontal => ({
        align: 'center',
        position: 'center',
        verticalAlign: 'middle',
        shape: 'square',
        x: undefined,
        y: undefined,
      })
    };

    export const Outside: DataLabelPosition = {
      id: 'outside',
      title: 'Outside',
      dataLabelsOptions: isHorizontal => (isHorizontal ? {
        align: 'left',
        position: 'right',
        verticalAlign: undefined,
        shape: 'callout',
        x: +labelOffset,
        y: undefined,
      } : {
        align: 'center',
        position: undefined,
        verticalAlign: 'bottom',
        shape: 'callout',
        x: undefined,
        y: -labelOffset,
      })
    };

    export const Inside: DataLabelPosition = {
      id: 'inside',
      title: 'Inside',
      dataLabelsOptions: isHorizontal => (isHorizontal ? {
        align: 'right',
        position: 'left',
        verticalAlign: undefined,
        shape: 'callout',
        x: -labelOffset,
        y: undefined,
      } : {
        align: 'center',
        position: undefined,
        verticalAlign: 'top',
        shape: 'callout',
        x: undefined,
        y: +labelOffset,
      })
    };
  }


  export interface LegendStyle extends TextElementStyle {
    readonly position: LegendPosition;
  }

  export namespace LegendStyle {
    export const defaultValue: LegendStyle = {
      ...TextStyle.Default,
      fontSize: {
        value: 12,
        kind: 'px',
      },
      enabled: false,
      position: LegendPosition.Bottom
    };

    export function SerializationOptions(defaultValue: LegendStyle) {
      return {
        read: s => ({
          ...defaultValue,

          ...TextElementStyle.serializationOptions.read({
            ...defaultValue,
            ...s,
            fontSize: {
              ...defaultValue?.fontSize,
              ...s?.fontSize
            }
          }),

          position:
            (s?.position
              ? LegendPosition.options.find(l => l.id == s.position)
              : null) ?? LegendPosition.options[0],
        }),
        write: s => ({
          ...TextElementStyle.serializationOptions.write(s),
          position: s?.position?.id
        }),
        defaultValue
      };
    }

    export interface Serialized extends TextElementStyle {
      readonly position: string;
    }
  }


  export interface DataLabelsStyle extends TextElementStyle {
    readonly backgroundColor?: string;
    readonly borderColor?: string;
    readonly numberFormat?: NumberFormat;
  }

  export namespace DataLabelsStyle {
    export const serializationOptions: SerializationOptions<DataLabelsStyle, Serialized> = {
      read: s => ({ ...TextElementStyle.serializationOptions.read(s), }),
      write: s => ({ ...TextElementStyle.serializationOptions.write(s), }),
    };

    export interface Serialized extends TextElementStyle {
      readonly position: string;
    }
  }


  export class Style extends VisualStyle {
    @Serialize(TextElementStyle.SerializationOptions({ ...TextStyle.Default, enabled: true }))
    readonly axisLabels = signal<TextElementStyle>(null);

    @Serialize(TextElementStyle.SerializationOptions({ ...TextStyle.Default, enabled: true }))
    readonly xAxis = signal<TextElementStyle>(null);

    @Serialize(TextElementStyle.SerializationOptions({ ...TextStyle.Default, enabled: true }))
    readonly yAxis = signal<TextElementStyle>(null);

    @Serialize(LegendStyle.SerializationOptions(LegendStyle.defaultValue))
    readonly legend = signal<LegendStyle>(null);

    @Serialize(DataLabelsStyle.serializationOptions)
    readonly dataLabels = signal<DataLabelsStyle>(null);

    
    @Serialize()
    readonly axisLabelsModified = signal<boolean>(false);

    @Serialize()
    readonly xAxisModified = signal<boolean>(false);

    @Serialize()
    readonly yAxisModified = signal<boolean>(false);

    @Serialize()
    readonly legendModified = signal<boolean>(false);

    @Serialize()
    readonly dataLabelsModified = signal<boolean>(false);


    protected override afterDeserialized(context: VisualContext) {
      let visualTypeOptions = context.visualData()?.type?.options?.visualOptions as Options;

      if (!this.legendModified()) 
        this.legend.update(x => ({ ...x, enabled: !!visualTypeOptions.legendEnabledByDefault }));

      if (!this.dataLabelsModified()) 
        this.dataLabels.update(x => ({ ...x, enabled: !!visualTypeOptions.dataLabelsEnabledByDefault }));

      super.afterDeserialized(context);
    }
  }
  //#endregion


  export interface Options extends Visual.Options {
    readonly noXAxis?: boolean;
    readonly noYAxis?: boolean;
    readonly noLegend?: boolean;
    readonly noDataLabels?: boolean;
    readonly dataLabelPosition?: DataLabelPosition;

    readonly legendEnabledByDefault?: boolean;
    readonly dataLabelsEnabledByDefault?: boolean;
  }

  export function defineType<
    TComponent extends ChartVisualComponent<TSeries, TOptions, TState, TStyle>,
    TSeries extends SeriesOptions = SeriesOptions,
    TOptions extends Options = ReturnType<TypeOfValue<TComponent, 'options'>>,
    TState extends State = ReturnType<TypeOfValue<TComponent, 'state'>>,
    TStyle extends Style = ReturnType<TypeOfValue<TComponent, 'style'>>,
    TVisual extends Visual<TOptions, TState, TStyle> = Visual<TOptions, TState, TStyle>,
    TRoles extends VisualFieldRoleTypeMap = VisualFieldRoleTypeMap>(
      component: ComponentType<TComponent>,
      name: string,
      roles: TRoles,
      typeOptions?: VisualTypeOptions<TOptions>
    ) {
    return VisualType.define<TComponent, TOptions, TState, TStyle, TVisual, TRoles>(
      component, name, roles,
      {
        canReuse: false,
        styleEditor: ChartStyleEditorComponent,
        fieldSorting: VisualFieldSorting.single(),
        ...typeOptions,
        createStyle: (options, fields) => {
          let style = <Style>(
            typeOptions?.createStyle
              ? typeOptions.createStyle(options, fields)
              : new Style());

          if (options.dataLabelsEnabledByDefault) {
            style.dataLabels.set({
              ...style.dataLabels(),
              enabled: true,
            });
          }
          
          if (options.legendEnabledByDefault) {
            style.legend.set({
              ...style.legend(),
              enabled: true,
            });
          }

          return style;
        }
      });
  }
}


export enum CustomLegendMode {
  PerSeries,
  PerPoint,
  PerGrouping,
}

export class CustomLegend {
  constructor(readonly chart: ChartVisualComponent) { }

  readonly redraw = signal(0);
  readonly enabled = computed(() => CUSTOM_LEGEND && this.chart.legendStyle()?.enabled && !this.chart.options()?.noLegend);
  readonly style = computed(() => this.chart.legendStyle());

  readonly position = computed(() => this.style()?.position ?? ChartVisual.LegendStyle.defaultValue.position);
  readonly className = computed(() => this.position()?.className ?? '');

  get items() { return this.chart.customLegendItems; }

  onSelection(e: Highcharts.SelectEventObject) {
    timer(0).pipe(
      takeUntilDestroyed(this.chart.destroyRef),
      tap(() => this.redraw.update(x => x + 1))
    );
  }

  readonly isHorizontal = computed(() =>
    ![ChartVisual.LegendPosition.Left, ChartVisual.LegendPosition.Right].includes(this.position()));

  readonly legendScrollWidth = computed(() => {
    this.redraw();
    return this.chart.legendScrollElement()?.nativeElement?.offsetWidth ?? 0;
  });

  readonly legendScrollHeight = computed(() => {
    this.redraw();
    return this.chart.legendScrollElement()?.nativeElement?.offsetHeight ?? 0;
  });

  readonly legendItemsWidth = computed(() => {
    this.redraw();
    return this.chart.legendItemsElement()?.nativeElement?.offsetWidth ?? 0 - 50;
  });

  readonly legendItemsScrollWidth = computed(() => {
    this.redraw();
    return this.chart.legendItemsElement()?.nativeElement?.scrollWidth ?? 0;
  });

  readonly legendItemsHeight = computed(() => {
    this.redraw();
    return this.chart.legendItemsElement()?.nativeElement?.offsetWidth ?? 0 - 50;
  });

  readonly legendItemsScrollHeight = computed(() => {
    this.redraw();
    return this.chart.legendItemsElement()?.nativeElement?.scrollHeight ?? 0;
  });

  readonly needsNavHorizontally = computed(() => this.legendItemsScrollWidth()  > this.legendItemsWidth());
  readonly needsNavVertically   = computed(() => this.legendItemsScrollHeight() > this.legendItemsHeight());

  navClick(direction: number) {
    let horizontal = this.isHorizontal();
    let back = direction < 0;

    let element = this.chart.legendItemsElement()?.nativeElement;
    if (element) {
      let elementRect = element.getBoundingClientRect();

      let item = element.firstElementChild as HTMLElement;

      while (item.nextElementSibling) {
        let itemRect = item.getBoundingClientRect();

        if (horizontal) {
          if (itemRect.right >= elementRect.left)
            break;
        }
        else {
          if (itemRect.bottom >= elementRect.top)
            break;
        }

        item = item.nextElementSibling as HTMLElement;
      }

      if (back) {
        if (item.previousSibling)
          item = item.previousElementSibling as HTMLElement;
      }
      else {
        if (item.nextElementSibling)
          item = item.nextElementSibling as HTMLElement;
      }

      let first = element.firstElementChild.getBoundingClientRect();

      if (this.isHorizontal()) {
        let offset = item.getBoundingClientRect().left - first.left;
        element.scrollTo(offset, 0);
      }
      else {
        let offset = item.getBoundingClientRect().top - first.top;
        element.scrollTo(0, offset);
      }
    }
  }

  itemClick(item: CustomLegendItem) {
    item.setVisible(!item.visible());
    this.chart.chart()?.chart()?.redraw(false);
  }
}

let nextCustomLegendItemId = 1;

export interface CustomLegendTarget {
  getColor(): any;
  getName(): string;

  isSelected(): boolean;
  setSelected(value: boolean);

  isVisible(): boolean;
  setVisible(value: boolean);
}

export class HighchartPointsCustomLegendTarget implements CustomLegendTarget {
  constructor(
    readonly points: ReadonlyArray<Point>,
    readonly name?: string,
    readonly color?: string) { }

  getColor() { return this.color ?? this.points[0].color; }
  getName() { return this.name ?? this.points[0].name; }

  isSelected() { return this.points[0].selected; }
  setSelected(value: boolean) { this.points.forEach(x => this.pointSetSelected(x, value)); }

  isVisible() { return this.points[0].visible; }
  setVisible(value: boolean) { this.points.forEach(x => this.pointSetVisible(x, value)); }
  
  protected pointSetSelected(p: Point, value: boolean) {
    p.select(value);
  }

  protected pointSetVisible(p: Point, value: boolean) {
    p.setVisible(value);
  }
}

export class HighchartPointCustomLegendTarget implements CustomLegendTarget {
  constructor(readonly point: Point) { }

  getColor() { return this.point.color; }
  getName() { return this.point.name; }

  isSelected() { return this.point.selected; }
  setSelected(value: boolean) { this.point.select(value); }

  isVisible() { return this.point.visible; }
  setVisible(value: boolean) { this.point.setVisible(value); }
}

export class HighchartSeriesCustomLegendTarget implements CustomLegendTarget {
  constructor(readonly series: Series) { }

  getColor() { return this.series.color; }
  getName() { return this.series.name; }

  isSelected() { return this.series.selected; }
  setSelected(value: boolean) { this.series.select(value); }

  isVisible() { return this.series.visible; }
  setVisible(value: boolean) { this.series.setVisible(value); }
}

export class CustomLegendItem {
  constructor(readonly target: CustomLegendTarget) { }

  readonly id = nextCustomLegendItemId++;
  readonly color = computed(() => this.target.getColor());
  readonly name = computed(() => this.target.getName());
  readonly selected = signal(this.target.isSelected());
  readonly visible = signal(this.target.isVisible());

  setSelected(value: boolean) {
    untracked(() => {
      if (value == this.selected())
        return;

      this.selected.set(value);
      this.target.setSelected(value);
    });
  }

  setVisible(value: boolean) {
    untracked(() => {
      if (value == this.visible())
        return;

      this.visible.set(value);
      this.target.setVisible(value);
    });
  }

  update() {
    this.selected.set(this.target.isSelected());
    this.visible.set(this.target.isVisible());
  }
}


export interface ChartLegendItem {
  readonly legend: ChartLegend;

  readonly index: number;
  readonly id: any;

  readonly raw: any;
  readonly data: any;
  readonly display: string;

  readonly rows: any[];
}


const NULL_KEY = {};

export class ChartLegend {
  private readonly _itemMap = new Map<any, ChartLegendItem>();
  private _items: ChartLegendItem[] = [];

  get items(): ReadonlyArray<ChartLegendItem> {
    return this._items;
  }

  constructor(
    public readonly field: VisualField,
    public readonly formatter: VisualComponent.RowFormatter) { }
    
  getOrAddItem(value: any): ChartLegendItem {
    return this.findItem(value) ?? this.addItem(value);
  }

  addItem(value: any): ChartLegendItem {
    let formatter = this.formatter;

    let item: ChartLegendItem = {
      legend: this,

      index: this._itemMap.size,
      id: `_${this._itemMap.size}`,

      raw: value,
      data: formatter.getValueData(this.field, value),
      display: formatter.getValueDisplay(this.field, value),

      rows: []
    };

    this._itemMap.set(value ?? NULL_KEY, item);
    this._items.push(item);

    return item;
  }

  findItem(value: any): ChartLegendItem {
    value ??= NULL_KEY;
    return this._itemMap.get(value);
  }

  addItemsFromRows(rows: Rows) {
    let count = rows.length;
    let dataIndex = this.formatter.columns.get(this.field).dataIndex;

    for (let i = 0; i < count; ++i) {
      let row = rows[i];
      let value = row[dataIndex];
      let item = this.getOrAddItem(value);
      item.rows.push(row);
    }
  }
}


export interface TooltipPointData {
  p?: Highcharts.Point;
  readonly valueDisplay: string | (() => string);
  readonly parent?: TooltipParentData;
} 

export interface TooltipFormatArgs extends TooltipPointData {
  readonly groupings: ReadonlyArray<VisualField>;
  readonly groupingsDisplay: ReadonlyArray<string>;
  readonly measure: VisualField;
  readonly dontReuse?: boolean;
  readonly groupingKey: any;
  readonly dataPoint: DataPoint;
  readonly shared?: boolean;
}

export type DataLabelData = { text: string; } | { value: any; field: VisualField; displayFormatArgs?: Partial<DisplayFormatArgs>; }

export type DataTooltipPointCustomData = (
  { dataPoint: DataPoint; } |
  { dataPoints: ReadonlyArray<DataPoint>; } |
  { dataTooltip: DataTooltip; }
) & {
  dataLabel: DataLabelData;
};

export interface TooltipCustomData extends TooltipFormatArgs {
  points?: TooltipPointData[];
  tooltip?: any;
  tooltipVersion?: number;
}

export interface TooltipParentData {
  readonly title: string;
  readonly display: string;
}

export function formatPercentage(value: number) {
  let str =
    (value < 1) ? value.toFixed(2) :
    (value < 2) ? value.toFixed(1) : value.toFixed(0);
    
  if (str.endsWith('00%')) {
    str = str.replace(/[,. ]00%$/, '%');
  }

  return str;
}


export interface ISeriesField {
  readonly options: CreateSeriesOptions;
  readonly index: number;
  readonly field: VisualField;
  readonly dataIndex: number;
}

export class SeriesField implements ISeriesField {
  constructor(
    readonly options: CreateSeriesOptions,
    readonly index: number,
    readonly field: VisualField,
    readonly dataIndex: number) { }
}

export class SeriesLegendField extends SeriesField {
  constructor(
    options: CreateSeriesOptions,
    index: number,
    field: VisualField,
    dataIndex: number,
    readonly legend: ChartLegend) { super(options, index, field, dataIndex); }
}

export class CreateSeries {
  constructor(
    readonly options: CreateSeriesOptions,
    readonly measure: SeriesField,
    readonly rows: Rows,
    readonly legend?: SeriesLegendField,
    readonly legendItem?: ChartLegendItem) { }
}

export interface CreatePoint {
  readonly series: CreateSeries;
  readonly row: Values;
  readonly colorIndex: number;
}

export type ChartPoint =
  Partial<Highcharts.PointOptionsObject> & 
  { custom?: any; } & 
  Record<string, unknown>;

export type ChartSeries<T extends SeriesOptions> = 
  (Partial<T> | { data?: ChartPoint[]; }) & 
  Record<string, unknown>;

export class CreateSeriesOptions {
  _groupings: SeriesField[] = [];
  _measures: SeriesField[] = [];
  _legends: SeriesLegendField[] = [];

  _groupingsDataIndexCache: number[] = [];
  _measuresDataIndexCache: number[] = [];
  _legendsDataIndexCache: number[] = [];

  get groupings(): ReadonlyArray<SeriesField> { return this._groupings; }
  get measures(): ReadonlyArray<SeriesField> { return this._measures; }
  get legends(): ReadonlyArray<SeriesLegendField> { return this._legends; }

  constructor(
    public readonly result: RowsResult,
    public readonly formatter: VisualComponent.RowFormatter) { }

  addGrouping(index: number, field: VisualField) {
    this._groupings.push(
      new SeriesField(
        this, 
        index, 
        field, 
        this.result.columns.get(field).dataIndex));

    this._groupingsDataIndexCache.push(undefined);
  }
  
  addMeasure(index: number, field: VisualField) {
    this._measures.push(
      new SeriesField(
        this,
        index, 
        field,
        this.result.columns.get(field).dataIndex));

    this._measuresDataIndexCache.push(undefined);
  }

  addLegend(index: number, legend: ChartLegend) {
    this._legends.push(
      new SeriesLegendField(
        this, 
        index,
        legend.field,
        this.result.columns.get(legend.field).dataIndex,
        legend));

    this._legendsDataIndexCache.push(undefined);
  }


  allGroupingsDisplay(row: ReadonlyArray<any>): ReadonlyArray<string> {
    return this.groupings.map((_, i) => this.groupingDisplay(i, row));
  }

  allGroupingsData(row: ReadonlyArray<any>): ReadonlyArray<any> {
    return this.groupings.map((_, i) => this.groupingData(i, row));
  }

  allMeasuresDisplay(row: ReadonlyArray<any>): ReadonlyArray<string> {
    return this.measures.map((_, i) => this.measureDisplay(i, row));
  }

  allMeasuresData(row: ReadonlyArray<any>): ReadonlyArray<any> {
    return this.measures.map((_, i) => this.measureData(i, row));
  }

  allLegendDisplay(row: ReadonlyArray<any>): ReadonlyArray<string> {
    return this.legends.map((_, i) => this.legendDisplay(i, row));
  }

  allLegendData(row: ReadonlyArray<any>): ReadonlyArray<any> {
    return this.legends.map((_, i) => this.legendData(i, row));
  }


  groupingDisplay(i: number, row: ReadonlyArray<any>): string {
    let g = this.groupings[i];
    return this.formatter.getRowValueDisplay(row, g.field);
  }

  groupingData(i: number, row: ReadonlyArray<any>): any {
    let g = this.groupings[i];
    return this.formatter.getValueData(g.field, row[g.dataIndex]);
  }

  measureDisplay(i: number, row: ReadonlyArray<any>): string {
    let m = this.measures[i];
    return this.formatter.getRowValueDisplay(row, m.field);
  }

  measureData(i: number, row: ReadonlyArray<any>): any {
    let m = this.measures[i];
    return this.formatter.getValueData(m.field, row[m.dataIndex]);
  }

  legendDisplay(i: number, row: ReadonlyArray<any>): string {
    let l = this.legends[i];
    return this.formatter.getRowValueDisplay(row, l.field);
  }

  legendData(i: number, row: ReadonlyArray<any>): any {
    let l = this.legends[i];
    return this.formatter.getValueData(l.field, row[l.dataIndex]);
  }


  get grouping() { return this.groupings[0]; }
  get measure() { return this.measures[0]; }

  firstGroupingDisplay(row: ReadonlyArray<any>): string {
    return this.groupingDisplay(0, row);
  }

  firstGroupingData(row: ReadonlyArray<any>): any {
    return this.groupingData(0, row);
  }

  firstMeasureDisplay(row: ReadonlyArray<any>): string {
    return this.measureDisplay(0, row);
  }

  firstMeasureData(row: ReadonlyArray<any>): any {
    return this.measureData(0, row);
  }
}
